import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BsArrowLeft, BsPlusLg } from "react-icons/bs";
import Sidebar from "../Sidebar";
import Header from "../Header";
import API from "../../config";
import "./GhlManage.css";

const getToken = () => localStorage.getItem("userToken");

const simOptions = (dev) => {
  const s = String(dev.selected_sim || "SIM1").toUpperCase();
  return s === "BOTH" ? ["SIM1", "SIM2"] : [s];
};

export default function GhlManage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sel, setSel] = useState({ sms: {}, whatsapp: {}, imessage: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // Kick off the GHL OAuth flow to connect another sub-account.
  const connectGhl = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.get(`${API}/ghl/oauth/url`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("GHL connect URL failed:", e);
    } finally {
      setConnecting(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ghl/assignments`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setData(data);
      setSelected((prev) => prev || data.installations[0]?.location_id || null);
    } catch (e) {
      console.error("GHL assignments load failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Build the editable selection whenever the sub-account (or data) changes.
  useEffect(() => {
    if (!data || !selected) return;
    const a = data.assignments[selected] || { sms: [], whatsapp: [], imessage: [] };
    const mk = (arr) => {
      const m = {};
      for (const x of arr) m[x.sender_id] = { on: true, sim: x.sim_slot || "SIM1", def: !!x.is_default };
      return m;
    };
    setSel({ sms: mk(a.sms), whatsapp: mk(a.whatsapp), imessage: mk(a.imessage) });
  }, [data, selected]);

  const toggle = (chan, id) => setSel((p) => {
    const cur = p[chan][id] || { on: false, sim: "SIM1", def: false };
    const on = !cur.on;
    return { ...p, [chan]: { ...p[chan], [id]: { ...cur, on, def: on ? cur.def : false } } };
  });
  const setSim = (id, sim) => setSel((p) => ({
    ...p, sms: { ...p.sms, [id]: { ...(p.sms[id] || { on: true, def: false }), sim } },
  }));
  const setDefault = (chan, id) => setSel((p) => {
    const cur = p[chan][id];
    if (!cur || !cur.on) return p;
    const cleared = {};
    for (const k of Object.keys(p[chan])) cleared[k] = { ...p[chan][k], def: false };
    cleared[id] = { ...cur, def: true };
    return { ...p, [chan]: cleared };
  });

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    const pack = (chan, withSim) => Object.entries(sel[chan])
      .filter(([, v]) => v.on)
      .map(([id, v]) => withSim
        ? { sender_id: id, sim_slot: v.sim, is_default: v.def }
        : { sender_id: id, is_default: v.def });
    try {
      await axios.put(`${API}/ghl/assignments`, {
        location_id: selected,
        sms: pack("sms", true),
        whatsapp: pack("whatsapp", false),
        imessage: pack("imessage", false),
      }, { headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" } });
      setSavedMsg(true);
      setTimeout(() => setSavedMsg(false), 2500);
      await load();
    } catch (e) {
      console.error("GHL assignments save failed:", e);
    }
    setSaving(false);
  };

  const summaryFor = (locId) => {
    const a = data?.assignments?.[locId];
    if (!a) return "nothing assigned";
    const p = [];
    if (a.sms.length) p.push(`${a.sms.length} device${a.sms.length > 1 ? "s" : ""}`);
    if (a.whatsapp.length) p.push(`${a.whatsapp.length} number${a.whatsapp.length > 1 ? "s" : ""}`);
    if (a.imessage.length) p.push(`${a.imessage.length} handle${a.imessage.length > 1 ? "s" : ""}`);
    return p.join(" · ") || "nothing assigned";
  };

  const senders = data?.senders || { sms: [], whatsapp: [], imessage: [] };
  const activeSel = sel;

  const renderChannel = (title, iconBi, iconClass, chan, list, withSim, labelOf, onlineOf) => {
    const assignedCount = Object.values(activeSel[chan] || {}).filter((v) => v.on).length;
    return (
      <div className="gm-chan">
        <div className="gm-chan-hd">
          <span className={`gm-ic ${iconClass}`}><i className={`bi ${iconBi}`} /></span>
          <span className="t">{title}</span>
          <span className="c">{assignedCount} of {list.length} assigned</span>
        </div>
        <div className="gm-rows">
          {list.length === 0 && <div className="gm-empty">No {title.toLowerCase()} connected yet.</div>}
          {list.map((s) => {
            const v = activeSel[chan][s.id] || { on: false, sim: "SIM1", def: false };
            return (
              <div key={s.id} className={`gm-row ${v.on ? "sel" : ""} ${v.def ? "def" : ""}`}>
                <span className="gm-chk" onClick={() => toggle(chan, s.id)}>{v.on ? "✓" : ""}</span>
                <span className="gm-nm">{labelOf(s)} <small>{onlineOf(s) ? "· Online" : "· Offline"}</small></span>
                {withSim && (
                  <select className="gm-sim" value={v.sim} disabled={!v.on} onChange={(e) => setSim(s.id, e.target.value)}>
                    {simOptions(s).map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                )}
                <button className="gm-def" disabled={!v.on} onClick={() => setDefault(chan, s.id)}>
                  <span className="rd" />Default
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="page-container">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="container mt-4 gm-scope">
          <button className="gm-back" onClick={() => navigate("/integration")}><BsArrowLeft /> Integrations</button>
          <div className="gm-head-row">
            <h4 className="gm-title">GoHighLevel — assign senders to sub-accounts</h4>
            <button className="gm-connect" onClick={connectGhl} disabled={connecting}>
              <BsPlusLg /> {connecting ? "Opening…" : "Connect sub-account"}
            </button>
          </div>

          <div className="gm-howto">
            <div className="gm-howto-t">ℹ️ How this works</div>
            <ol>
              <li><b>Pick a sub-account</b> on the left.</li>
              <li><b>Tick the senders it may use</b> — SMS devices, WhatsApp numbers, iMessage handles. Only ticked ones appear in that sub-account's GHL workflow <b>trigger &amp; action</b> dropdowns.</li>
              <li><b>Choose one Default per channel</b> — used for the contact <b>Conversation</b> area when a reply is sent without picking a sender. In workflows, the sender is chosen per action.</li>
              <li><b>Save</b>, then repeat per sub-account. Sub-accounts with nothing assigned fall back to all company senders.</li>
            </ol>
          </div>

          {loading ? (
            <div className="gm-loading">Loading…</div>
          ) : !data || data.installations.length === 0 ? (
            <div className="gm-empty-page">
              No GoHighLevel sub-accounts connected yet.
              <button className="gm-connect" onClick={connectGhl} disabled={connecting}>
                <BsPlusLg /> {connecting ? "Opening…" : "Connect GoHighLevel"}
              </button>
            </div>
          ) : (
            <div className="gm-card">
              <div className="gm-rail">
                <div className="gm-rail-hd">Sub-accounts · {data.installations.length}</div>
                {data.installations.map((inst) => (
                  <div
                    key={inst.location_id}
                    className={`gm-acct ${selected === inst.location_id ? "active" : ""}`}
                    onClick={() => setSelected(inst.location_id)}
                  >
                    <span className="nm">{inst.location_name || inst.location_id}</span>
                    <span className="meta">{summaryFor(inst.location_id)}</span>
                  </div>
                ))}
                <button className="gm-add-acct" onClick={connectGhl} disabled={connecting}>
                  <BsPlusLg /> {connecting ? "Opening…" : "Add sub-account"}
                </button>
              </div>

              <div className="gm-detail">
                {selected && (
                  <>
                    <h5 className="gm-acct-name">
                      {data.installations.find((i) => i.location_id === selected)?.location_name || selected}
                    </h5>
                    <p className="gm-loc">Location ID: {selected}</p>
                    <div className="gm-banner">
                      <b>Ticked</b> = what this sub-account can use (fills the workflow dropdowns). <b>Default</b> = the sender used in the contact Conversation area when none is picked.
                    </div>

                    {renderChannel("SMS devices", "bi-chat-dots-fill", "gm-ic-sms", "sms", senders.sms, true,
                      (s) => s.name, (s) => s.status === "active")}
                    {renderChannel("WhatsApp numbers", "bi-whatsapp", "gm-ic-wa", "whatsapp", senders.whatsapp, false,
                      (s) => s.label, (s) => s.status === "connected")}
                    {renderChannel("iMessage handles", "bi-apple", "gm-ic-im", "imessage", senders.imessage, false,
                      (s) => s.label, (s) => s.status === "online")}

                    <div className="gm-bar">
                      <span className="gm-sum">{savedMsg ? "✓ Saved" : summaryFor(selected)}</span>
                      <button className="gm-save" onClick={save} disabled={saving}>
                        {saving ? "Saving…" : "Save assignment"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
